import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import LayersIcon from '@mui/icons-material/Layers';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DescriptionIcon from '@mui/icons-material/Description';
import TuneIcon from '@mui/icons-material/Tune';
import { INVENTORY_COLORS, UNIT_STATUS } from '../../../../../../utils/inventory_helper';
import ConfigurationDetailsFields from '../../../ConfigurationDetailsFields';
import InventoryKeyHighlighter from '../../../../../../components/inventory/InventoryKeyHighlighter';
import InventoryAreaFields, { getAreaRowSlug, PRESET_AREA_OPTIONS } from '../../../../../../components/inventory/InventoryAreaFields';

const AREA_UNIT_OPTIONS = [
  { value: 'sqft', label: 'sqft' },
  { value: 'sqmt', label: 'sq.mt' },
  { value: 'acres', label: 'Acres' },
];

function slugifyLabel(s) {
  return (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^\w-]/g, '_')
    .replace(/^_|_$/g, '') || '';
}

function formatINR(amount) {
  if (amount == null || isNaN(amount)) return '—';
  if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(2).replace(/\.?0+$/, '')} L`;
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function formatCommissionOneDecimal(rate) {
  if (rate == null || rate === '' || isNaN(Number(rate))) return '—';
  const n = Number(rate);
  return `${n % 1 === 0 ? n : n.toFixed(1)}%`;
}

function slugToTitleCase(s) {
  if (s == null || typeof s !== 'string') return '';
  return s.trim().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim() || s;
}

function formatMetadataItem(item) {
  if (item == null) return '—';
  if (typeof item === 'object') return JSON.stringify(item);
  return String(item);
}

function CardHeader({
  title,
  onEdit,
  onSave,
  onCancel,
  isEditing,
  editLabel = 'Edit',
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  editStyle = 'grey',
  icon,
  saveDisabled,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.75,
        py: 1.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography
          variant="overline"
          fontWeight={700}
          sx={{ letterSpacing: 1, fontSize: '0.6875rem', color: 'text.secondary' }}
        >
          {title}
        </Typography>
      </Box>
      {isEditing ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {onCancel && (
            <Box
              component="button"
              type="button"
              onClick={onCancel}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'grey.300',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                bgcolor: 'transparent',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {cancelLabel}
            </Box>
          )}
          {onSave && (
            <Box
              component="button"
              type="button"
              onClick={onSave}
              disabled={saveDisabled}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 2.5,
                border: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: saveDisabled ? 'not-allowed' : 'pointer',
                bgcolor: INVENTORY_COLORS.green,
                color: '#fff',
                opacity: saveDisabled ? 0.7 : 1,
                '&:hover': saveDisabled ? {} : { bgcolor: INVENTORY_COLORS.greenMid },
              }}
            >
              {saveLabel}
            </Box>
          )}
        </Box>
      ) : (
        onEdit && (
          <Box
            component="button"
            type="button"
            onClick={onEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: 2.5,
              border: '1px solid',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              borderColor: 'grey.300',
              bgcolor: 'grey.100',
              color: 'text.primary',
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
            {editLabel}
          </Box>
        )
      )}
    </Box>
  );
}

const detailFieldStyles = {
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1 },
  '& .MuiInputLabel-root': { fontSize: 12 },
};

const BOOKING_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'booked', label: 'Booked' },
  { value: 'hold', label: 'On Hold' },
  { value: 'sold', label: 'Sold' },
  { value: 'blocked', label: 'Blocked' },
];

// Backend Unit model: primary fields = name, unit_code, inventory_status, price, area, commission; rest = metadata (JSON).
// Other Details = single key-value list from metadata; facing & tower_wing are shown in Basic Details only.
const OTHER_DETAILS_EXCLUDE_KEYS = ['tags', 'facing', 'tower_wing', 'unit_block'];
// For getParamDefsFromOptions and schema filtering (still used elsewhere in file).
const DEDICATED_FIELD_KEYS = ['floor', 'facing', 'tower_wing'];
const SCHEMA_DEDICATED_KEYS = ['floor', 'facing', 'tower_wing', 'tower'];
const RESERVED_META_KEYS = ['floor', 'facing', 'tower_wing', 'tower', 'amenities', 'facilities', 'features', 'tags', 'optional_fields'];

const AREA_SCHEMA_KEYWORDS = ['area', 'sqft', 'sq.ft', 'carpet', 'builtup', 'built-up', 'built_up', 'super'];
function isSchemaAreaField(field) {
  if (!field) return false;
  const h = `${field.label ?? ''} ${field.key ?? ''}`.toLowerCase();
  return AREA_SCHEMA_KEYWORDS.some((kw) => h.includes(kw));
}
/** Param (from unitParameterOptions) is area-type → show only in Unit details (schema), not in primary "Area & parameters". */
function isParamAreaField(param) {
  if (!param) return false;
  const h = `${param.label ?? ''} ${param.id ?? ''}`.toLowerCase();
  return AREA_SCHEMA_KEYWORDS.some((kw) => h.includes(kw));
}

/** Build param defs from options API (value/label), excluding dedicated fields so they are not shown twice. */
function getParamDefsFromOptions(unitParameterOptions) {
  const list = Array.isArray(unitParameterOptions) ? unitParameterOptions : [];
  const seen = new Set();
  return list
    .filter((p) => {
      const id = (p.value ?? p.id ?? '').toString().trim();
      return id && !DEDICATED_FIELD_KEYS.includes(id) && !seen.has(id) && (seen.add(id), true);
    })
    .map((p) => ({ id: (p.value ?? p.id ?? '').toString(), label: p.label ?? p.name ?? p.value ?? p.id ?? '' }));
}

/** Map param key -> normalized label from options API. Includes alias tower -> Tower / Wing to avoid duplicate display. */
function getParamLabelMap(unitParameterOptions) {
  const list = Array.isArray(unitParameterOptions) ? unitParameterOptions : [];
  const map = {};
  list.forEach((p) => {
    const id = (p.value ?? p.id ?? '').toString().trim();
    const label = p.label ?? p.name ?? p.value ?? p.id ?? id;
    if (id) map[id] = label;
  });
  const towerWingLabel = map.tower_wing || 'Tower / Wing';
  if (towerWingLabel && !map.tower) map.tower = towerWingLabel;
  const superAreaLabel = map.super_area || 'Super Built-up Area (sq.ft)';
  if (superAreaLabel && !map.super_builtup_area) map.super_builtup_area = superAreaLabel;
  return map;
}

/** Format param value for display; avoid [object Object] for objects/arrays. */
function formatParamDisplayValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      if (value.length === 0) return '—';
      const first = value[0];
      if (typeof first === 'object' && first !== null) return `${value.length} item(s)`;
      return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
    }
    return JSON.stringify(value);
  }
  return String(value);
}

const UnitOverview = ({
  unit,
  project,
  config,
  areaUnitOptions = [],
  unitStatusOptions = [],
  unitParameterOptions = [],
  facingOptions = [],
  isBasicDetailsEditing,
  onEditBasicDetails,
  onSaveBasicDetails,
  onCancelBasicDetails,
  basicDetailsSaving,
  isAreaEditing,
  onEditArea,
  onSaveArea,
  onCancelArea,
  areaSaving,
  isKeyHighlightsEditing,
  onEditKeyHighlights,
  onSaveKeyHighlights,
  onCancelKeyHighlights,
  keyHighlightsSaving,
  isOtherDetailsEditing,
  onEditOtherDetails,
  onSaveOtherDetails,
  onCancelOtherDetails,
  otherDetailsSaving,
  isCommissionEditing,
  onEditCommission,
  onSaveCommission,
  onCancelCommission,
  commissionSaving,
  isTagsEditing,
  onEditTags,
  onSaveTags,
  onCancelTags,
  tagsSaving,
}) => {
  const areaUnitOpts = areaUnitOptions?.length ? areaUnitOptions.map((o) => ({ value: o.value ?? o.id ?? o, label: o.label ?? o.name ?? o.value ?? o })) : AREA_UNIT_OPTIONS;
  const statusOpts = unitStatusOptions?.length ? unitStatusOptions.map((s) => ({ value: (s.value ?? s.id ?? s).toString().toLowerCase(), label: s.label ?? s.name ?? s })) : BOOKING_STATUS_OPTIONS;
  const paramDefs = getParamDefsFromOptions(unitParameterOptions);
  const paramLabelMap = getParamLabelMap(unitParameterOptions);
  const paramDefsNonArea = useMemo(() => paramDefs.filter((p) => !isParamAreaField(p)), [paramDefs]);

  const [name, setName] = useState('');
  const [unitCode, setUnitCode] = useState('');
  const [floor, setFloor] = useState('');
  const [facing, setFacing] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [area, setArea] = useState('');
  const [areaUnit, setAreaUnit] = useState('sqft');
  const [areaRows, setAreaRows] = useState([]);
  const [unitBlock, setUnitBlock] = useState('');
  const [keyHighlightsDraft, setKeyHighlightsDraft] = useState(['']);
  const [amenitiesDraft, setAmenitiesDraft] = useState([]);
  const [facilitiesDraft, setFacilitiesDraft] = useState([]);
  const [featuresDraft, setFeaturesDraft] = useState([]);
  const [newAmenity, setNewAmenity] = useState('');
  const [newFacility, setNewFacility] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [tagsDraft, setTagsDraft] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [commissionDraft, setCommissionDraft] = useState('');
  const [bookingStatus, setBookingStatus] = useState('available');
  const [paramValues, setParamValues] = useState({});
  const [customParams, setCustomParams] = useState([]);
  const [paramIdsToEdit, setParamIdsToEdit] = useState([]);
  const [schemaAttributesDraft, setSchemaAttributesDraft] = useState({});
  const [otherDetailsRows, setOtherDetailsRows] = useState([]);

  const schemaSections = useMemo(() => {
    const raw = unit?.unit_schema?.sections ?? [];
    if (!Array.isArray(raw)) return [];
    return [...raw]
      .filter((s) => s && typeof s === 'object' && s.key !== 'unit_loan')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [unit?.unit_schema?.sections]);
  const meta = unit?.metadata || {};
  const unitLevelArea = unit?.area ?? unit?.total_area;
  const unitAreaNum = useMemo(() => {
    if (Array.isArray(unitLevelArea) && unitLevelArea.length > 0) {
      const first = unitLevelArea[0];
      const v = first?.value ?? first;
      return v != null && v !== '' ? Number(v) : null;
    }
    return unitLevelArea != null && unitLevelArea !== '' ? Number(unitLevelArea) : null;
  }, [unitLevelArea]);
  const schemaSectionsFiltered = useMemo(() => {
    return schemaSections.map((sec) => ({
      ...sec,
      fields: (sec.fields || []).filter((f) => {
        const key = (f.key ?? slugifyLabel(f.label) ?? '').toString().trim();
        if (SCHEMA_DEDICATED_KEYS.includes(key)) return false;
        if (isSchemaAreaField(f) && unitAreaNum != null) {
          const raw = meta[key] ?? meta[slugifyLabel(f.label)];
          const val = typeof raw === 'object' && raw !== null && 'value' in raw ? raw.value : raw;
          if (val != null && val !== '' && Number(val) === unitAreaNum) return false;
        }
        return true;
      }),
    })).filter((sec) => (sec.fields || []).length > 0);
  }, [schemaSections, meta, unitAreaNum]);
  const schemaFieldKeys = useMemo(() => {
    const set = new Set();
    schemaSections.forEach((sec) => (sec.fields || []).forEach((f) => set.add((f.key ?? slugifyLabel(f.label) ?? '').toString().trim())));
    return set;
  }, [schemaSections]);
  const hasSchemaSections = schemaSectionsFiltered.length > 0;
  const filteredSchemaFieldKeys = useMemo(() => {
    const set = new Set();
    schemaSectionsFiltered.forEach((sec) => (sec.fields || []).forEach((f) => set.add((f.key ?? slugifyLabel(f.label) ?? '').toString().trim())));
    return set;
  }, [schemaSectionsFiltered]);
  const schemaValueForForm = useMemo(() => {
    const out = {};
    filteredSchemaFieldKeys.forEach((k) => { if (schemaAttributesDraft[k] !== undefined) out[k] = schemaAttributesDraft[k]; });
    return out;
  }, [schemaAttributesDraft, filteredSchemaFieldKeys]);

  useEffect(() => {
    if (!unit?.id || !schemaSections?.length) {
      setSchemaAttributesDraft({});
      return;
    }
    const meta = unit.metadata || {};
    const attrs = {};
    schemaSections.forEach((sec) => {
      (sec.fields || []).forEach((f) => {
        const key = (f.key ?? slugifyLabel(f.label) ?? '').toString().trim();
        if (!key) return;
        const raw = meta[key] ?? meta[slugifyLabel(f.label)];
        if (raw !== undefined && raw !== null) {
          attrs[key] = typeof raw === 'object' && raw !== null && 'value' in raw ? { ...raw } : { value: raw };
        }
      });
    });
    setSchemaAttributesDraft(attrs);
  }, [unit?.id, unit?.metadata, schemaSections]);

  const facingOpts = useMemo(() => {
    if (facingOptions?.length) return facingOptions.map((o) => ({ value: (o.value ?? o.id ?? o).toString(), label: o.label ?? o.name ?? o.value ?? o }));
    return [
      { value: 'n', label: 'North' }, { value: 'ne', label: 'North-East' }, { value: 'e', label: 'East' },
      { value: 'se', label: 'South-East' }, { value: 's', label: 'South' }, { value: 'sw', label: 'South-West' },
      { value: 'w', label: 'West' }, { value: 'nw', label: 'North-West' },
    ];
  }, [facingOptions]);

  useEffect(() => {
    if (isBasicDetailsEditing && unit) {
      setName(unit.name ?? '');
      setBasePrice(unit.price != null ? String(unit.price) : '');
      setBookingStatus((unit.inventory_status || 'available').toString().toLowerCase());
      setFloor(unit.floor != null && unit.floor !== '' ? String(unit.floor) : (unit.metadata?.floor != null ? String(unit.metadata.floor) : ''));
      setFacing(unit.facing ?? unit.metadata?.facing ?? '');
      setUnitBlock(unit.unit_block ?? unit.metadata?.tower_wing ?? '');
    }
  }, [isBasicDetailsEditing, unit?.id, unit?.name, unit?.price, unit?.inventory_status, unit?.floor, unit?.facing, unit?.unit_block, unit?.metadata]);

  useEffect(() => {
    if (!isAreaEditing || !unit) return;
    const areaVal = unit.area;
    const presetLabel = (k) => PRESET_AREA_OPTIONS.find((p) => p.value === k)?.label ?? slugToTitleCase(k);
    if (Array.isArray(areaVal) && areaVal.length > 0) {
      setAreaRows(areaVal.map((row) => {
        const key = row.key || row.slug || 'carpet_area';
        return {
          key,
          label: presetLabel(key),
          value: row.value != null ? String(row.value) : '',
          unit: row.unit || unit.area_unit || 'sqft',
        };
      }));
      setAreaUnit(areaVal[0]?.unit || unit.area_unit || 'sqft');
    } else {
      const single = (unit.area ?? unit.total_area) != null ? String(unit.area ?? unit.total_area) : '';
      setArea(single);
      setAreaRows(single ? [{ key: 'carpet_area', label: 'Carpet Area', value: single, unit: unit.area_unit || 'sqft' }] : []);
      setAreaUnit(unit.area_unit || 'sqft');
    }
  }, [isAreaEditing, unit?.id, unit?.area, unit?.total_area, unit?.area_unit]);

  useEffect(() => {
    if (isKeyHighlightsEditing && unit) {
      const raw = Array.isArray(unit.key_highlights) ? unit.key_highlights : [];
      setKeyHighlightsDraft(raw.length > 0 ? raw.map((h) => String(h).trim()) : ['']);
    }
  }, [isKeyHighlightsEditing, unit?.id, unit?.key_highlights]);

  // Serialize metadata value to string for edit field (array -> comma-separated, object with value -> value + unit).
  const metaValueToEditString = (v) => {
    if (v == null || v === '') return '';
    if (Array.isArray(v)) return v.map((item) => (typeof item === 'object' && item != null && 'value' in item ? item.value : item)).join(', ');
    if (typeof v === 'object' && v !== null && 'value' in v) return `${v.value}${v.unit != null ? ` ${v.unit}` : ''}`;
    return String(v);
  };

  useEffect(() => {
    if (isOtherDetailsEditing && unit) {
      const meta = unit.metadata || {};
      const rows = Object.keys(meta)
        .filter((k) => !OTHER_DETAILS_EXCLUDE_KEYS.includes(k))
        .map((k) => ({
          id: `od-${k}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          key: k,
          value: metaValueToEditString(meta[k]),
        }));
      setOtherDetailsRows(rows.length ? rows : [{ id: `od-new-${Date.now()}`, key: '', value: '' }]);
    }
  }, [isOtherDetailsEditing, unit?.id, unit?.metadata]);

  const areaTypeParamIds = useMemo(() => new Set(paramDefs.filter(isParamAreaField).map((p) => p.id)), [paramDefs]);
  const paramDefsToEdit = paramDefsNonArea.filter((p) => paramIdsToEdit.includes(p.id));
  const paramDefsToAdd = paramDefsNonArea.filter((p) => !paramIdsToEdit.includes(p.id));
  const addParamToEdit = (id) => {
    if (!paramIdsToEdit.includes(id)) {
      setParamIdsToEdit((prev) => [...prev, id]);
      setParamValues((prev) => ({ ...prev, [id]: '' }));
    }
  };

  useEffect(() => {
    if (isTagsEditing) {
      const tags = unit?.tags ?? unit?.metadata?.tags ?? [];
      setTagsDraft(Array.isArray(tags) ? [...tags] : []);
      setNewTagInput('');
    }
  }, [isTagsEditing, unit?.id]);

  useEffect(() => {
    if (isCommissionEditing) {
      const rate = unit?.commission_rate ?? unit?.metadata?.commission_rate;
      setCommissionDraft(rate != null && rate !== '' ? String(rate) : '');
    }
  }, [isCommissionEditing, unit?.id]);

  const handleSaveBasicDetails = () => {
    const floorNum = floor.trim() ? (Number(floor.trim()) || null) : null;
    onSaveBasicDetails?.({
      name: name.trim() || unit?.name,
      price: basePrice.trim() ? parseFloat(basePrice) : null,
      inventory_status: bookingStatus || undefined,
      floor: floorNum,
      facing: facing.trim() || null,
      unit_block: unitBlock.trim() || null,
    });
  };

  const handleSaveArea = () => {
    const areaPayload = areaRows.length > 0
      ? areaRows
          .filter((r) => r && getAreaRowSlug(r) && r.value != null && r.value !== '' && !Number.isNaN(parseFloat(r.value)))
          .map((r) => ({ key: getAreaRowSlug(r), value: parseFloat(r.value), unit: (r.unit || areaUnit || 'sqft').toString() }))
      : (area.trim() ? [{ key: 'carpet_area', value: parseFloat(area), unit: areaUnit || 'sqft' }] : null);
    onSaveArea?.({ area: areaPayload, area_unit: areaUnit || null });
  };

  const handleSaveKeyHighlights = () => {
    const list = (keyHighlightsDraft || []).map((h) => String(h).trim()).filter(Boolean);
    onSaveKeyHighlights?.(list);
  };

  const handleSaveOtherDetails = () => {
    const metadata = {};
    otherDetailsRows.forEach(({ key, value }) => {
      const k = String(key).trim();
      const v = value != null ? String(value).trim() : '';
      if (k && v !== '') metadata[k] = v;
    });
    onSaveOtherDetails?.({ metadata });
  };

  const addAmenity = () => {
    const t = newAmenity.trim();
    if (t && !amenitiesDraft.includes(t)) setAmenitiesDraft((prev) => [...prev, t]);
    setNewAmenity('');
  };

  const removeAmenity = (index) => {
    setAmenitiesDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addFacility = () => {
    const t = newFacility.trim();
    if (t && !facilitiesDraft.includes(t)) setFacilitiesDraft((prev) => [...prev, t]);
    setNewFacility('');
  };

  const removeFacility = (index) => {
    setFacilitiesDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addFeature = () => {
    const t = newFeature.trim();
    if (t && !featuresDraft.includes(t)) setFeaturesDraft((prev) => [...prev, t]);
    setNewFeature('');
  };

  const removeFeature = (index) => {
    setFeaturesDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addCustomParam = () => {
    setCustomParams((prev) => [...prev, { id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`, key: '', value: '' }]);
  };

  const updateCustomParam = (id, field, val) => {
    setCustomParams((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: val } : p)));
  };

  const removeCustomParam = (id) => {
    setCustomParams((prev) => prev.filter((p) => p.id !== id));
  };

  const addOtherDetailsRow = () => {
    setOtherDetailsRows((prev) => [...prev, { id: `od-${Date.now()}-${Math.random().toString(36).slice(2)}`, key: '', value: '' }]);
  };
  const updateOtherDetailsRow = (id, field, val) => {
    setOtherDetailsRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  };
  const removeOtherDetailsRow = (id) => {
    setOtherDetailsRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length ? next : [{ id: `od-new-${Date.now()}`, key: '', value: '' }];
    });
  };

  const definedParamKeys = new Set(paramDefs.map((p) => p.id.toLowerCase()));
  const isCustomKeyDuplicate = (key, currentId) => {
    const k = String(key).trim().toLowerCase();
    if (!k) return false;
    if (definedParamKeys.has(k)) return true;
    const others = customParams.filter((p) => p.id !== currentId);
    return others.some((p) => String(p.key).trim().toLowerCase() === k);
  };
  const hasDuplicateCustomKey = customParams.some((p) => isCustomKeyDuplicate(p.key, p.id));

  const setParamValue = (paramId, value) => {
    setParamValues((prev) => ({ ...prev, [paramId]: value }));
  };

  const addTag = () => {
    const t = newTagInput.trim().toLowerCase();
    if (t && !tagsDraft.includes(t)) setTagsDraft((prev) => [...prev, t]);
    setNewTagInput('');
  };

  const removeTag = (index) => {
    setTagsDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveTags = () => {
    onSaveTags?.(tagsDraft);
  };

  const isValidCommission = () => {
    if (commissionDraft === '') return true;
    const n = parseFloat(commissionDraft);
    return !isNaN(n) && n >= 0 && n <= 100;
  };

  const handleSaveCommission = () => {
    if (!isValidCommission()) return;
    onSaveCommission?.(commissionDraft);
  };

  const tags = unit?.tags ?? [];
  const hasUnitCommission = unit?.commission_rate != null && unit.commission_rate !== '';
  const effectiveRate = hasUnitCommission ? unit.commission_rate : (config?.commission ?? project?.commission_rate ?? (typeof project?.commission === 'string' ? parseFloat(project.commission) : project?.commission));
  const displayRate = formatCommissionOneDecimal(effectiveRate);
  const commissionLabel = hasUnitCommission ? 'Unit commission' : 'Default commission';
  const commissionSubtitle = hasUnitCommission ? 'Custom rate for this unit' : 'Inherited from configuration or project';

  const chipSx = {
    fontSize: '0.75rem',
    fontWeight: 500,
    py: 0.6,
    borderRadius: 2.5,
    bgcolor: INVENTORY_COLORS.greenLight,
    color: INVENTORY_COLORS.green,
    border: '1px solid',
    borderColor: INVENTORY_COLORS.greenAccent + '99',
    '& .MuiChip-label': { px: 1.5 },
  };

  const statusKey = (unit?.inventory_status || 'available').toString().toLowerCase();
  const statusStyle = UNIT_STATUS[statusKey] || UNIT_STATUS.available;

  if (!unit) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No unit data to display.</Typography>
      </Box>
    );
  }

  const areaListDisplay = Array.isArray(unit?.area_list) && unit.area_list.length > 0
    ? unit.area_list
    : (Array.isArray(unit?.area) ? unit.area.map((r) => `${slugToTitleCase(r.key || 'area')}: ${r.value} ${r.unit || 'sqft'}`) : []);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      {/* Left: Basic Details, Area, Key Highlights, Other Details */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* ── 1. Basic Details ───────────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Basic Details"
            onEdit={onEditBasicDetails}
            onSave={isBasicDetailsEditing ? handleSaveBasicDetails : undefined}
            onCancel={isBasicDetailsEditing ? onCancelBasicDetails : undefined}
            isEditing={isBasicDetailsEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={basicDetailsSaving}
            editStyle="grey"
            icon={
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ViewModuleIcon sx={{ fontSize: 16, color: INVENTORY_COLORS.green }} />
              </Box>
            }
          />
          <Box sx={{ p: 2 }}>
            {isBasicDetailsEditing ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={detailFieldStyles} />
                <FormControl size="small" sx={detailFieldStyles}>
                  <Select value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)} displayEmpty renderValue={(v) => statusOpts.find((o) => o.value === v)?.label ?? v}>
                    {statusOpts.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" label="Base price (₹)" type="number" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }} sx={detailFieldStyles} />
                <FormControl size="small" sx={detailFieldStyles}>
                  <InputLabel>Facing (optional)</InputLabel>
                  <Select value={facing} onChange={(e) => setFacing(e.target.value)} displayEmpty label="Facing (optional)" renderValue={(v) => facingOpts.find((o) => o.value === v)?.label ?? v ?? '—'}>
                    <MenuItem value="">—</MenuItem>
                    {facingOpts.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" label="Floor (optional)" type="number" value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="e.g. 2 — leave blank for plot/farmland" inputProps={{ min: 0 }} sx={detailFieldStyles} />
                <TextField size="small" label="Unit block (optional)" value={unitBlock} onChange={(e) => setUnitBlock(e.target.value)} placeholder="e.g. B, Block A — leave blank for plot/farmland" sx={detailFieldStyles} />
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Name</Typography>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{unit?.name || unit?.unit_code || '—'}</Typography>
                </Box>
                <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Status</Typography>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{statusOpts.find((o) => o.value === (unit?.inventory_status || 'available'))?.label ?? unit?.inventory_status ?? '—'}</Typography>
                </Box>
                {(unit?.price != null && unit?.price !== '') && (
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Sale Price</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: INVENTORY_COLORS.green }}>{formatINR(unit.price)}</Typography>
                  </Box>
                )}
                <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Area</Typography>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                    {areaListDisplay.length > 0 ? areaListDisplay[0] : (Array.isArray(unit?.area) && unit.area[0] ? `${unit.area[0].value} ${unit.area[0].unit || 'sqft'}` : (unit?.area != null && !Array.isArray(unit.area) ? `${Number(unit.area).toLocaleString('en-IN')} ${unit?.area_unit || 'sqft'}` : '—'))}
                  </Typography>
                </Box>
                {(unit?.facing ?? unit?.metadata?.facing) && (
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Facing</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{facingOpts.find((o) => (o.value || '').toString().toLowerCase() === ((unit.facing ?? unit.metadata?.facing) || '').toString().toLowerCase())?.label ?? slugToTitleCase((unit.facing ?? unit.metadata?.facing) || '')}</Typography>
                  </Box>
                )}
                {(unit?.unit_block ?? unit?.metadata?.tower_wing) && (
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Unit block</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{unit.unit_block ?? unit.metadata?.tower_wing}</Typography>
                  </Box>
                )}
                {(unit?.floor != null && unit?.floor !== '') && (
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Floor</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{unit.floor}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Card>

        {/* ── 2. Area ───────────────────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Area"
            onEdit={onEditArea}
            onSave={isAreaEditing ? handleSaveArea : undefined}
            onCancel={isAreaEditing ? onCancelArea : undefined}
            isEditing={isAreaEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={areaSaving}
            editStyle="grey"
            icon={<SquareFootIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2 }}>
            {isAreaEditing ? (
              <InventoryAreaFields
                value={areaRows}
                onChange={setAreaRows}
                areaUnit={areaUnit}
                areaUnitOptions={areaUnitOpts}
                accent="info"
                sx={{ '& .SectionHeader-root': { mt: 0 } }}
              />
            ) : (
              <>
                {areaListDisplay.length > 0 ? areaListDisplay.map((line, i) => <Typography key={i} variant="body2" sx={{ fontWeight: 500 }}>{line}</Typography>) : <Typography variant="body2" color="text.secondary">—</Typography>}
              </>
            )}
          </Box>
        </Card>


         {/* Tags — right column */}
         <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title="Tags"
            onEdit={onEditTags}
            onSave={isTagsEditing ? handleSaveTags : undefined}
            onCancel={isTagsEditing ? onCancelTags : undefined}
            isEditing={isTagsEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={tagsSaving}
            editStyle="grey"
            icon={<LayersIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isTagsEditing ? (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {tagsDraft.map((tag, index) => (
                    <Chip key={`${tag}-${index}`} label={tag} size="small" onDelete={() => removeTag(index)} sx={{ ...chipSx, '& .MuiChip-deleteIcon': { fontSize: 16 } }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField size="small" placeholder="New tag" value={newTagInput} onChange={(e) => setNewTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} sx={{ ...detailFieldStyles, flex: '1 1 120px', minWidth: 140, '& .MuiInputBase-root': { height: 36 } }} />
                  <Box component="button" type="button" onClick={addTag} disabled={!newTagInput.trim()} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderRadius: 1.25, border: 'none', fontSize: '0.8125rem', fontWeight: 600, cursor: newTagInput.trim() ? 'pointer' : 'not-allowed', bgcolor: INVENTORY_COLORS.green, color: '#fff', opacity: newTagInput.trim() ? 1 : 0.6 }}><AddIcon sx={{ fontSize: 18 }} /> Add</Box>
                </Box>
              </>
            ) : (
              <>
                {Array.isArray(tags) && tags.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{tags.map((t) => <Chip key={`tag-${t}`} label={t} size="small" sx={chipSx} />)}</Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No tags added yet.</Typography>
                )}
              </>
            )}
          </Box>
        </Card>

        {/* ── 4. Other Details (metadata, schema, params, amenities, etc.) ── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Other Details"
            onEdit={onEditOtherDetails}
            onSave={isOtherDetailsEditing ? handleSaveOtherDetails : undefined}
            onCancel={isOtherDetailsEditing ? onCancelOtherDetails : undefined}
            isEditing={isOtherDetailsEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={otherDetailsSaving}
            editStyle="grey"
            icon={<DescriptionIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
              Add features, amenities, facilities or any key : value.
            </Typography>
            {isOtherDetailsEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {otherDetailsRows.map((row) => (
                  <Box key={row.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      placeholder="e.g. Floor, Amenities"
                      label="Key"
                      value={paramLabelMap[row.key] || slugToTitleCase(row.key)}
                      onChange={(e) => updateOtherDetailsRow(row.id, 'key', slugifyLabel(e.target.value))}
                      sx={{ ...detailFieldStyles, minWidth: 160 }}
                    />
                    <TextField size="small" placeholder="Value" value={row.value} onChange={(e) => updateOtherDetailsRow(row.id, 'value', e.target.value)} sx={{ ...detailFieldStyles, flex: 1 }} />
                    <Box component="button" type="button" onClick={() => removeOtherDetailsRow(row.id)} sx={{ p: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'grey.300', color: 'error.main', cursor: 'pointer', bgcolor: 'transparent', mt: 0.5, '&:hover': { bgcolor: 'grey.100' } }}>Remove</Box>
                  </Box>
                ))}
                <Box component="button" type="button" onClick={addOtherDetailsRow} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: 1, border: '1px dashed', borderColor: 'grey.400', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', color: 'text.secondary', bgcolor: 'transparent', '&:hover': { bgcolor: 'grey.50' } }}><AddIcon sx={{ fontSize: 16 }} /> Add row</Box>
              </Box>
            ) : (
              <Box>
                {(() => {
                  const formatMetaValue = (v) => {
                    if (v == null) return '—';
                    if (Array.isArray(v)) return v.map((item) => formatMetadataItem(item)).join(', ');
                    if (typeof v === 'object' && v !== null && 'value' in v) return `${formatParamDisplayValue(v.value)}${v.unit ? ` ${v.unit}` : ''}`;
                    return formatParamDisplayValue(v);
                  };
                  const rows = Object.keys(meta)
                    .filter((k) => !OTHER_DETAILS_EXCLUDE_KEYS.includes(k))
                    .map((k) => ({
                      key: k,
                      label: paramLabelMap[k] || slugToTitleCase(k),
                      value: formatMetaValue(meta[k]),
                    }))
                    .filter((r) => r.value !== '—' && r.value !== '');
                  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No other details. Add features, amenities, facilities or any key : value.</Typography>;
                  return (
                    <Box sx={{ mt: 0.5 }}>
                      {rows.map(({ key, label, value }) => (
                        <Box
                          key={key}
                          sx={{
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {/* Right: Commission */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title="Commission Structure"
            onEdit={onEditCommission}
            onSave={isCommissionEditing ? handleSaveCommission : undefined}
            onCancel={isCommissionEditing ? onCancelCommission : undefined}
            isEditing={isCommissionEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={commissionSaving || (isCommissionEditing && !isValidCommission())}
            editStyle="grey"
            icon={<CurrencyRupeeIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ mx: 2.75, mt: 2, mb: 0, p: 2.5, borderRadius: 1.5, background: `linear-gradient(135deg, ${INVENTORY_COLORS.green} 0%, ${INVENTORY_COLORS.greenMid} 100%)`, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: -8, right: -8, width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', display: 'block', mb: 1 }}>{commissionLabel}</Typography>
            {isCommissionEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="number"
                  value={commissionDraft}
                  onChange={(e) => setCommissionDraft(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, sx: { bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, maxWidth: 140 } }}
                  size="small"
                  placeholder="0–100"
                  error={commissionDraft !== '' && !isValidCommission()}
                  helperText={commissionDraft !== '' && !isValidCommission() ? 'Enter 0–100' : ''}
                  sx={{ '& .MuiInputBase-root': { color: 'grey.900' }, '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.9)' } }}
                />
              </Box>
            ) : (
              <>
                <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '3.25rem', fontWeight: 700, color: '#fff', lineHeight: 1, letterSpacing: '-1px' }}>{displayRate}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', mt: 1 }}>{commissionSubtitle}</Typography>
              </>
            )}
          </Box>
          <Box sx={{ px: 2.75, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1.25, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '80' }}>
              <Typography variant="body2" fontWeight={700} color="text.primary">Default commission applied</Typography>
              <Typography variant="body1" fontWeight={700} sx={{ color: INVENTORY_COLORS.green }}>{formatCommissionOneDecimal(effectiveRate)}</Typography>
            </Box>
          </Box>
          {!hasUnitCommission && (
            <Box sx={{ mx: 2.75, mb: 2, p: 1.5, borderRadius: 1.25, bgcolor: '#fffbeb', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: '#ea580c', flexShrink: 0, mt: 0.25 }} />
              <Typography variant="caption" sx={{ color: '#92400e', lineHeight: 1.6 }}>Set a custom commission above to override the default for this unit only.</Typography>
            </Box>
          )}
        </Card>


        {/* ── 3. Key Highlights ──────────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Key Highlights"
            onEdit={onEditKeyHighlights}
            onSave={isKeyHighlightsEditing ? handleSaveKeyHighlights : undefined}
            onCancel={isKeyHighlightsEditing ? onCancelKeyHighlights : undefined}
            isEditing={isKeyHighlightsEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={keyHighlightsSaving}
            editStyle="grey"
            icon={<LayersIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2 }}>
            {isKeyHighlightsEditing ? (
              <InventoryKeyHighlighter value={keyHighlightsDraft?.length ? keyHighlightsDraft : ['']} onChange={setKeyHighlightsDraft} label="Key highlights" placeholder="e.g. Prime location, 24/7 security" sx={{ mt: 0 }} />
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Array.isArray(unit?.key_highlights) && unit.key_highlights.length > 0 ? unit.key_highlights.map((h, i) => <Chip key={i} label={h} size="small" sx={chipSx} />) : <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>No key highlights added.</Typography>}
                </Box>
              </>
            )}
          </Box>
        </Card>

       
      </Box>
    </Box>
  );
};

export default UnitOverview;
